import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { subscriptionService } from '../services/subscription';
import { authService } from '../services/auth';

/**
 * Componente guard que verifica si el usuario tiene suscripción activa.
 * Si no tiene suscripción activa, redirige a /subscription (pantalla de pago).
 * 
 * Se aplica a rutas que requieren suscripción activa para acceder.
 * No se aplica a: /login, /signup, /subscription, rutas públicas.
 */
const RequireSubscription = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        // Verificar si el usuario está autenticado
        const hasToken = authService.isAuthenticated();
        
        if (!hasToken) {
          // Si no está autenticado, dejar que ProtectedRoute maneje la redirección
          setHasAccess(false);
          setLoading(false);
          return;
        }

        // Obtener información del usuario para verificar si es admin
        const user = await authService.getCurrentUserFromBackend();
        
        if (!user) {
          // Usuario no autenticado, dejar que ProtectedRoute maneje
          setHasAccess(false);
          setLoading(false);
          return;
        }

        // Los admins no necesitan suscripción
        if (user.role === 'admin') {
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // Verificar estado de suscripción
        try {
          const statusResponse = await subscriptionService.getStatus();
          
          // Determinar si tiene acceso premium
          // Usar can_access_premium si existe, sino usar has_active_subscription, sino usar is_active
          const canAccessPremium = 
            statusResponse?.data?.can_access_premium ?? 
            statusResponse?.data?.has_active_subscription ?? 
            statusResponse?.data?.is_active ?? 
            false;

          if (canAccessPremium) {
            setHasAccess(true);
          } else {
            // No tiene suscripción activa - redirigir a pantalla de pago
            setHasAccess(false);
          }
        } catch (error) {
          // Si el error es "Usuario no tiene suscripción", es un caso válido
          if (error.message && error.message.includes('Usuario no tiene suscripción')) {
            setHasAccess(false);
          } else {
            // Para otros errores, por seguridad, también redirigir a pago
            console.error('Error verificando suscripción:', error);
            setHasAccess(false);
          }
        }
      } catch (error) {
        console.error('Error en verificación de suscripción:', error);
        // Por seguridad, si falla la verificación, redirigir a pago
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkSubscription();
  }, [location]);

  if (loading) {
    // Mostrar loading mientras se verifica la suscripción
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-lg">Verificando suscripción...</div>
      </div>
    );
  }

  if (!hasAccess) {
    // Redirigir a la pantalla de pago/suscripción
    // Guardar la ruta de origen para poder volver después de activar
    return <Navigate to="/subscription" state={{ from: location }} replace />;
  }

  return children;
};

export default RequireSubscription;

